import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Select, Space, Tag, Button, Typography, Empty, Alert } from 'antd';
import { SearchOutlined, ReloadOutlined, ClearOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { adminApi } from '../services/api';
import { Elenco, Voce } from '../components/Elenco';

const { Title, Text } = Typography;

interface User {
  id: number;
  name: string;
  email: string;
  business_name: string;
  subscription_plan: string;
  subscription_status: string;
  posts_count: number;
  created_at: string;
}

const STATO: Record<string, { t: string; c: string }> = {
  active:    { t: 'Attivo',      c: 'success' },
  trialing:  { t: 'In prova',    c: 'processing' },
  trial:     { t: 'In prova',    c: 'processing' },
  paused:    { t: 'In pausa',    c: 'warning' },
  canceling: { t: 'In disdetta', c: 'warning' },
  past_due:  { t: 'Non pagato',  c: 'error' },
  canceled:  { t: 'Chiuso',      c: 'default' },
  inactive:  { t: 'Inattivo',    c: 'default' },
};

const PIANO: Record<string, { t: string; c: string }> = {
  trial:   { t: 'Trial',   c: 'purple' },
  base:    { t: 'Base',    c: 'default' },
  medium:  { t: 'Medium',  c: 'blue' },
  premium: { t: 'Premium', c: 'green' },
};

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();

  // La ricerca parte quando si smette di scrivere: prima ogni tasto premuto
  // faceva una richiesta e il pannello sembrava lento.
  const [ricerca, setRicerca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setRicerca(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      const params: Record<string, string> = {};
      if (ricerca) params.search = ricerca;
      if (plan) params.plan = plan;
      if (status) params.status = status;
      const res = await adminApi.users(params);
      setUsers(res.data.data || []);
    } catch {
      setErrore('Non è stato possibile caricare gli utenti. Controlla la connessione e riprova.');
    } finally {
      setLoading(false);
    }
  }, [ricerca, plan, status]);

  useEffect(() => { load(); }, [load]);

  const filtriAttivi = Boolean(ricerca || plan || status);
  const azzera = () => { setSearch(''); setRicerca(''); setPlan(undefined); setStatus(undefined); };

  const colonne: ColumnsType<User> = [
    {
      title: 'Utente',
      dataIndex: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (_, u) => (
        <>
          <div style={{ fontWeight: 600, color: '#101527' }}>{u.name || '—'}</div>
          <Text type="secondary" style={{ fontSize: 12.5 }}>{u.email}</Text>
        </>
      ),
    },
    {
      title: 'Attività',
      dataIndex: 'business_name',
      responsive: ['md'],
      render: v => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Piano',
      dataIndex: 'subscription_plan',
      width: 110,
      render: p => {
        const x = PIANO[p];
        return x ? <Tag color={x.c}>{x.t}</Tag> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Stato',
      dataIndex: 'subscription_status',
      width: 130,
      render: s => {
        const x = STATO[s] || { t: s || '—', c: 'default' };
        return <Tag color={x.c}>{x.t}</Tag>;
      },
    },
    {
      title: 'Post',
      dataIndex: 'posts_count',
      width: 80,
      align: 'right',
      sorter: (a, b) => a.posts_count - b.posts_count,
    },
    {
      title: 'Iscritto il',
      dataIndex: 'created_at',
      width: 120,
      responsive: ['lg'],
      sorter: (a, b) => +new Date(a.created_at) - +new Date(b.created_at),
      render: d => new Date(d).toLocaleDateString('it-IT'),
    },
  ];

  return (
    <>
      <div className="page-top">
        <div>
          <Title level={3} style={{ margin: 0 }}>Utenti</Title>
          <Text type="secondary">
            {loading ? 'Caricamento…'
              : `${users.length} ${users.length === 1 ? 'utente' : 'utenti'}${filtriAttivi ? ' con questi filtri' : ''}`}
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Aggiorna</Button>
      </div>

      <Card
        styles={{ body: { padding: 0 } }}
        title={
          <Space wrap size={10} style={{ padding: '4px 0' }}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#98A1B8' }} />}
              placeholder="Cerca per nome, email o attività…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="filtro filtro-largo"
            />
            <Select
              allowClear placeholder="Tutti i piani" value={plan} onChange={setPlan}
              className="filtro"
              options={[
                { value: 'trial', label: 'Trial' }, { value: 'base', label: 'Base' },
                { value: 'medium', label: 'Medium' }, { value: 'premium', label: 'Premium' },
              ]}
            />
            <Select
              allowClear placeholder="Tutti gli stati" value={status} onChange={setStatus}
              className="filtro"
              options={[
                { value: 'active', label: 'Attivo' }, { value: 'paused', label: 'In pausa' },
                { value: 'canceling', label: 'In disdetta' }, { value: 'past_due', label: 'Non pagato' },
                { value: 'inactive', label: 'Inattivo' },
              ]}
            />
            {filtriAttivi && (
              <Button icon={<ClearOutlined />} onClick={azzera}>Azzera</Button>
            )}
          </Space>
        }
      >
        {errore && (
          <Alert
            type="error" showIcon message={errore} style={{ margin: 16 }}
            action={<Button size="small" onClick={load}>Riprova</Button>}
          />
        )}

        <Elenco<User>
          righe={users}
          colonne={colonne}
          loading={loading}
          rowKey={u => u.id}
          onRowClick={u => navigate(`/users/${u.id}`)}
          vuoto={
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={filtriAttivi
                ? 'Nessun utente con questi filtri'
                : 'Ancora nessun utente registrato'}
            >
              {filtriAttivi && <Button onClick={azzera}>Azzera i filtri</Button>}
            </Empty>
          }
          scheda={u => (
            <>
              <div className="scheda-titolo">{u.name || '—'}</div>
              <div className="scheda-sub">{u.email}</div>
              <Voce etichetta="Attività">{u.business_name || '—'}</Voce>
              <Voce etichetta="Piano">
                {PIANO[u.subscription_plan]
                  ? <Tag color={PIANO[u.subscription_plan].c}>{PIANO[u.subscription_plan].t}</Tag>
                  : '—'}
              </Voce>
              <Voce etichetta="Stato">
                <Tag color={(STATO[u.subscription_status] || { c: 'default' }).c}>
                  {(STATO[u.subscription_status] || { t: u.subscription_status || '—' }).t}
                </Tag>
              </Voce>
              <Voce etichetta="Post">{u.posts_count}</Voce>
            </>
          )}
        />
      </Card>
    </>
  );
}
